// ============================================================
// FILE: src/pages/QCHeadDashboardPage.jsx
// Redesigned: compact top bar, analytics-first, quick actions top
// ============================================================

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth }  from '../context/AuthContext';
import { supabase } from '../services/supabase';
import api          from '../services/api';
import { format, subDays, startOfDay, endOfDay, differenceInMinutes } from 'date-fns';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, CartesianGrid,
  AreaChart, Area, Legend, RadarChart, Radar, PolarGrid,
  PolarAngleAxis, PolarRadiusAxis,
} from 'recharts';
import { toast } from 'react-toastify';

const P  = '#6B21A8';
const PM = '#7C3AED';
const PL = '#EDE9FE';
const GR = '#16A34A';
const RD = '#DC2626';
const AM = '#D97706';

const DEPT_COLORS = {
  'Detergent'        : { bg:'#EDE9FE', color:'#6B21A8', border:'#C4B5FD' },
  'Soap'             : { bg:'#ECFDF5', color:'#059669', border:'#6EE7B7' },
  'Refinery'         : { bg:'#FFF7ED', color:'#EA580C', border:'#FDBA74' },
  'Boiler'           : { bg:'#F0F9FF', color:'#0369A1', border:'#7DD3FC' },
  'Filling & Packing': { bg:'#FEF9C3', color:'#854D0E', border:'#FDE68A' },
};

const REPORT_ITEMS = [
  { icon:'📚', label:'Report Books & Sign-off',     badge:'Core', path:'/report-books',        sub:'7 official lab record books with daily QC Head approval' },
  { icon:'⚠️', label:'OOS Investigation Log',        badge:null,   path:'/reports?tab=oos',     sub:'Out-of-spec history, root cause & corrective action tracker' },
  { icon:'📜', label:'Certificate of Analysis',      badge:'New',  path:'/reports?tab=coa',     sub:'Auto-generate COA documents for finished product batches' },
  { icon:'👤', label:'Analyst Performance',          badge:null,   path:'/reports?tab=analyst', sub:'Submissions, turnaround time, OOS rate & accuracy per analyst' },
  { icon:'📈', label:'SPC Control Charts',           badge:null,   path:'/spc-charts',          sub:'Shewhart X-bar & R charts — catch process drift before failure' },
  { icon:'🏭', label:'Batch Release Report',         badge:null,   path:'/reports?tab=batch',   sub:'Formal QC sign-off document for production batch release' },
  { icon:'💊', label:'Vitamin A Surveillance',       badge:null,   path:'/reports?tab=vita',    sub:'Daily Vitamin A results across all departments in one view' },
  { icon:'📊', label:'Trend & Statistical Analysis', badge:null,   path:'/trend-analysis',      sub:'7-day, 30-day & custom range charts for any test parameter' },
];

const ADMIN_ITEMS = [
  { icon:'👥', label:'User Management',            badge:null,  path:'/admin/users',         sub:'Add, edit, deactivate users · reset passwords · manage roles' },
  { icon:'🔬', label:'Test Specifications',        badge:null,  path:'/admin/test-specs',    sub:'Update min/max ranges for any test across all sample types' },
  { icon:'⚙️', label:'Instrument Calibration',    badge:null,  path:'/admin/calibration',   sub:'Log calibration dates, certificates & next-due alerts' },
  { icon:'📋', label:'Method Validation Records',  badge:'New', path:'/admin/methods',       sub:'Store and retrieve analytical method validation documentation' },
  { icon:'🔒', label:'Full Audit Trail',           badge:null,  path:'/admin/audit',         sub:'Tamper-proof log of every create, edit & delete in the system' },
  { icon:'🏗️', label:'Department & Sample Config', badge:null,  path:'/admin/dept-config',   sub:'Manage departments, categories, sample types & subtypes' },
  { icon:'🔔', label:'Notification Rules',         badge:null,  path:'/admin/notifications', sub:'Configure OOS alerts, escalation chains & SMS/email triggers' },
  { icon:'⚙️', label:'System Settings & Backup',  badge:null,  path:'/admin/settings',      sub:'Shifts, timezone, data retention, export & system health' },
];

const CHART_TYPES = [
  { key:'bar',    label:'Bar',    icon:'📊' },
  { key:'line',   label:'Line',   icon:'📈' },
  { key:'area',   label:'Area',   icon:'🌊' },
  { key:'pie',    label:'Pie',    icon:'🥧' },
  { key:'radar',  label:'Radar',  icon:'🕸️' },
];

export default function QCHeadDashboardPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);
  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth <= 1024);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);

  const [showReports, setShowReports] = useState(false);
  const [showAdmin,   setShowAdmin]   = useState(false);
  const reportsRef = useRef(null);
  const adminRef   = useRef(null);
  useEffect(() => {
    const fn = (e) => {
      if (reportsRef.current && !reportsRef.current.contains(e.target)) setShowReports(false);
      if (adminRef.current   && !adminRef.current.contains(e.target))   setShowAdmin(false);
    };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, []);

  // Data
  const [stats,        setStats]        = useState({ total:0, pending:0, running:0, complete:0, oos:0, passRate:100, stale:0 });
  const [depts,        setDepts]        = useState([]);
  const [oosFeed,      setOosFeed]      = useState([]);
  const [trend7,       setTrend7]       = useState([]);
  const [staleSamples, setStaleSamples] = useState([]);
  const [loading,      setLoading]      = useState(true);

  // Analytics chart type
  const [chartType, setChartType] = useState('bar');

  // Date filter — compact single line
  const [dateMode, setDateMode] = useState('day');
  const [selDate,  setSelDate]  = useState(format(new Date(),'yyyy-MM-dd'));
  const [fromDate, setFromDate] = useState(format(subDays(new Date(),6),'yyyy-MM-dd'));
  const [toDate,   setToDate]   = useState(format(new Date(),'yyyy-MM-dd'));

  // Assign Sample
  const [showAssign,    setShowAssign]    = useState(false);
  const [assignStep,    setAssignStep]    = useState(1);
  const [assignSearch,  setAssignSearch]  = useState('');
  const [assignSamples, setAssignSamples] = useState([]);
  const [assignSample,  setAssignSample]  = useState(null);
  const [assignNew,     setAssignNew]     = useState(false);
  const [staffList,     setStaffList]     = useState([]);
  const [assignAnalyst, setAssignAnalyst] = useState('');
  const [availTests,    setAvailTests]    = useState([]);
  const [selTests,      setSelTests]      = useState([]);
  const [assigning,     setAssigning]     = useState(false);
  const [newSampleForm, setNewSampleForm] = useState({ sample_name:'', dept_id:'', type_id:'', sampler_name:'' });
  const [deptList,      setDeptList]      = useState([]);
  const [typeList,      setTypeList]      = useState([]);

  const getRange = useCallback(() => {
    if (dateMode === 'day') return { start: startOfDay(new Date(selDate)).toISOString(), end: endOfDay(new Date(selDate)).toISOString() };
    return { start: startOfDay(new Date(fromDate)).toISOString(), end: endOfDay(new Date(toDate)).toISOString() };
  }, [dateMode, selDate, fromDate, toDate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { start, end } = getRange();
      const { data: samples } = await supabase
        .from('registered_samples')
        .select(`id,sample_name,sample_number,status,registered_at,
          departments(id,name,code),
          sample_test_assignments(id,result_value,result_status,submitted_at,tests(name))`)
        .gte('registered_at', start).lte('registered_at', end)
        .order('registered_at', { ascending:false });

      const all      = samples || [];
      const pending  = all.filter(s => s.status==='pending');
      const running  = all.filter(s => s.status==='in_progress');
      const complete = all.filter(s => s.status==='complete');
      const oosAll   = all.flatMap(s => (s.sample_test_assignments||[]).filter(a => a.result_status==='fail_low'||a.result_status==='fail_high'));
      const submitted = all.flatMap(s => (s.sample_test_assignments||[]).filter(a => a.result_value));
      const passRate  = submitted.length ? Math.round(((submitted.length-oosAll.length)/submitted.length)*100) : 100;

      const stale = all.filter(s => {
        if (s.status!=='in_progress') return false;
        const last = (s.sample_test_assignments||[]).filter(a=>a.submitted_at).sort((a,b)=>new Date(b.submitted_at)-new Date(a.submitted_at))[0];
        return differenceInMinutes(new Date(), new Date(last?last.submitted_at:s.registered_at)) > 120;
      });

      setStats({ total:all.length, pending:pending.length, running:running.length, complete:complete.length, oos:oosAll.length, passRate, stale:stale.length });
      setStaleSamples(stale.slice(0,5));

      // Dept map
      const dm = {};
      for (const s of all) {
        const d = s.departments; if (!d) continue;
        if (!dm[d.name]) dm[d.name] = { name:d.name, total:0, complete:0, oos:0 };
        dm[d.name].total++;
        if (s.status==='complete') dm[d.name].complete++;
        dm[d.name].oos += (s.sample_test_assignments||[]).filter(a=>a.result_status==='fail_low'||a.result_status==='fail_high').length;
      }
      setDepts(Object.values(dm));

      // OOS feed
      setOosFeed(all.flatMap(s =>
        (s.sample_test_assignments||[])
          .filter(a=>a.result_status==='fail_low'||a.result_status==='fail_high')
          .map(a=>({ sample_name:s.sample_name, sample_number:s.sample_number, test_name:a.tests?.name, result:a.result_value, status:a.result_status, time:a.submitted_at }))
      ).sort((a,b)=>new Date(b.time)-new Date(a.time)).slice(0,8));

      // 7-day trend
      const trend = [];
      for (let i=6; i>=0; i--) {
        const d = subDays(new Date(), i);
        const { data: dd } = await supabase.from('registered_samples').select('id,status').gte('registered_at',startOfDay(d).toISOString()).lte('registered_at',endOfDay(d).toISOString());
        const total    = (dd||[]).length;
        const complete = (dd||[]).filter(s=>s.status==='complete').length;
        const pending  = (dd||[]).filter(s=>s.status==='pending').length;
        trend.push({ day:format(d,'EEE'), date:format(d,'dd/MM'), total, complete, pending, running:total-complete-pending });
      }
      setTrend7(trend);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }, [getRange]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const sub = supabase.channel('qch_live')
      .on('postgres_changes',{event:'*',schema:'public',table:'registered_samples'},()=>load())
      .on('postgres_changes',{event:'*',schema:'public',table:'sample_test_assignments'},()=>load())
      .subscribe();
    return () => sub.unsubscribe();
  }, [load]);

  // Assign helpers
  const searchSamples = async (q) => {
    if (!q.trim()) { setAssignSamples([]); return; }
    const { data } = await supabase.from('registered_samples').select('id,sample_name,sample_number,status,departments(name),sample_types(id,name)').or(`sample_name.ilike.%${q}%,sample_number.ilike.%${q}%`).in('status',['pending','in_progress']).limit(10);
    setAssignSamples(data||[]);
  };
  const loadAssignTests = async (typeId) => {
    if (!typeId) return;
    const { data } = await supabase.from('tests').select('id,name,code,unit,test_specifications(display_spec)').eq('sample_type_id',typeId).order('display_order');
    setAvailTests(data||[]); setSelTests((data||[]).map(t=>t.id));
  };
  useEffect(() => { if (showAssign) { api.get('/lookup/staff?role=Analyst').then(r=>setStaffList(r.data?.staff||[])).catch(()=>{}); supabase.from('departments').select('id,name').then(({data})=>setDeptList(data||[])); } }, [showAssign]);
  useEffect(() => { if (newSampleForm.dept_id) supabase.from('sample_types').select('id,name').then(({data})=>setTypeList(data||[])); }, [newSampleForm.dept_id]);
  useEffect(() => { if (assignSample?.sample_types?.id) loadAssignTests(assignSample.sample_types.id); }, [assignSample]);
  useEffect(() => { if (newSampleForm.type_id) loadAssignTests(newSampleForm.type_id); }, [newSampleForm.type_id]);

  const handleAssign = async () => {
    if (!assignAnalyst) { toast.warning('Select an analyst'); return; }
    if (!selTests.length) { toast.warning('Select at least one test'); return; }
    setAssigning(true);
    try {
      let sampleId = assignSample?.id;
      if (assignNew) {
        const { data: s } = await supabase.from('registered_samples').insert({ sample_name:newSampleForm.sample_name, sample_number:`QCH-${Date.now()}`, department_id:newSampleForm.dept_id, sample_type_id:newSampleForm.type_id, sampler_name:newSampleForm.sampler_name||user?.full_name, status:'in_progress', registered_by:user?.id }).select('id').single();
        sampleId = s?.id;
      } else {
        await supabase.from('registered_samples').update({ status:'in_progress' }).eq('id', sampleId);
      }
      await supabase.from('sample_test_assignments').insert(selTests.map(testId=>({ sample_id:sampleId, test_id:testId, analyst_signature:assignAnalyst, assigned_by:user?.id })));
      toast.success(`✅ ${selTests.length} tests assigned to ${assignAnalyst}`);
      setShowAssign(false); setAssignStep(1); setAssignSample(null); setAssignNew(false); setAssignAnalyst(''); setSelTests([]);
      load();
    } catch(e) { toast.error(e.response?.data?.error||e.message); }
    finally { setAssigning(false); }
  };

  // Pie data from depts
  const deptPieData = depts.map(d => ({ name:d.name, value:d.total, color: DEPT_COLORS[d.name]?.color || PM }));

  // Pass/Fail pie
  const passPieData = [
    { name:'Pass', value: stats.complete - stats.oos, color: GR },
    { name:'OOS',  value: stats.oos,                  color: RD },
    { name:'Running', value: stats.running,            color: AM },
    { name:'Pending', value: stats.pending,            color: '#94A3B8' },
  ].filter(d=>d.value>0);

  const DropdownMenu = ({ items }) => (
    <div style={{ position:'fixed', top:'52px', left:'auto', minWidth:'360px', background:'#fff', borderRadius:'14px', boxShadow:'0 16px 48px rgba(107,33,168,0.32)', border:`1.5px solid ${PL}`, zIndex:99999, maxHeight:'80vh', overflowY:'auto' }}>
      {items.map((item,i) => (
        <button key={i} onClick={()=>{ navigate(item.path); setShowReports(false); setShowAdmin(false); }}
          style={{ display:'flex', alignItems:'flex-start', gap:'10px', width:'100%', padding:'10px 14px', border:'none', borderBottom:i<items.length-1?`1px solid ${PL}`:'none', background:'#fff', cursor:'pointer', fontFamily:'inherit', textAlign:'left' }}
          onMouseEnter={e=>e.currentTarget.style.background='#F5F3FF'}
          onMouseLeave={e=>e.currentTarget.style.background='#fff'}>
          <span style={{ fontSize:'18px', flexShrink:0 }}>{item.icon}</span>
          <div style={{ flex:1 }}>
            <div style={{ display:'flex', alignItems:'center', gap:'6px' }}>
              <span style={{ fontWeight:'700', fontSize:'12px', color:'#1F2937' }}>{item.label}</span>
              {item.badge && <span style={{ fontSize:'9px', fontWeight:'800', padding:'1px 5px', borderRadius:'5px', background:item.badge==='New'?'#FEF9C3':'#EDE9FE', color:item.badge==='New'?'#854D0E':P }}>{item.badge}</span>}
            </div>
            <div style={{ fontSize:'10px', color:'#6B7280', marginTop:'1px', lineHeight:1.4 }}>{item.sub}</div>
          </div>
          <span style={{ fontSize:'11px', color:'#9CA3AF' }}>→</span>
        </button>
      ))}
    </div>
  );

  const inp = { border:`1.5px solid ${PL}`, borderRadius:'8px', padding:'8px 11px', fontSize:'13px', fontFamily:'inherit', background:'#fff', color:'#111827', outline:'none', width:'100%', boxSizing:'border-box' };

  // Render the selected chart type for 7-day trend
  const renderTrendChart = () => {
    const h = 200;
    const commonProps = { data:trend7, margin:{ top:5, right:5, left:-20, bottom:0 } };
    const xAxis = <XAxis dataKey="day" tick={{ fontSize:10, fill:'#94A3B8' }} axisLine={false} tickLine={false}/>;
    const yAxis = <YAxis tick={{ fontSize:10, fill:'#94A3B8' }} axisLine={false} tickLine={false}/>;
    const grid  = <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false}/>;
    const tip   = <Tooltip contentStyle={{ borderRadius:'10px', border:`1px solid ${PL}`, fontSize:'11px' }}/>;
    const legend = <Legend wrapperStyle={{ fontSize:'11px' }}/>;

    if (chartType==='bar') return (
      <ResponsiveContainer width="100%" height={h}>
        <BarChart {...commonProps} barCategoryGap="30%" barGap={2}>
          {grid}{xAxis}{yAxis}{tip}{legend}
          <Bar dataKey="complete" name="Complete" fill={GR}       radius={[4,4,0,0]}/>
          <Bar dataKey="running"  name="Running"  fill={AM}       radius={[4,4,0,0]}/>
          <Bar dataKey="pending"  name="Pending"  fill="#94A3B8"  radius={[4,4,0,0]}/>
        </BarChart>
      </ResponsiveContainer>
    );
    if (chartType==='line') return (
      <ResponsiveContainer width="100%" height={h}>
        <LineChart {...commonProps}>
          {grid}{xAxis}{yAxis}{tip}{legend}
          <Line type="monotone" dataKey="total"    name="Total"    stroke={PM} strokeWidth={2} dot={{ r:3 }}/>
          <Line type="monotone" dataKey="complete" name="Complete" stroke={GR} strokeWidth={2} dot={{ r:3 }}/>
          <Line type="monotone" dataKey="pending"  name="Pending"  stroke="#94A3B8" strokeWidth={1.5} strokeDasharray="4 2" dot={false}/>
        </LineChart>
      </ResponsiveContainer>
    );
    if (chartType==='area') return (
      <ResponsiveContainer width="100%" height={h}>
        <AreaChart {...commonProps}>
          {grid}{xAxis}{yAxis}{tip}{legend}
          <Area type="monotone" dataKey="complete" name="Complete" stroke={GR} fill="#DCFCE7" strokeWidth={2}/>
          <Area type="monotone" dataKey="running"  name="Running"  stroke={AM} fill="#FEF9C3" strokeWidth={2}/>
          <Area type="monotone" dataKey="pending"  name="Pending"  stroke="#94A3B8" fill="#F1F5F9" strokeWidth={1.5}/>
        </AreaChart>
      </ResponsiveContainer>
    );
    if (chartType==='pie') return (
      <ResponsiveContainer width="100%" height={h}>
        <PieChart>
          <Pie data={passPieData} cx="50%" cy="50%" outerRadius={75} dataKey="value" nameKey="name" label={({name,percent})=>`${name} ${(percent*100).toFixed(0)}%`} labelLine={false} fontSize={10}>
            {passPieData.map((entry,i)=><Cell key={i} fill={entry.color}/>)}
          </Pie>
          <Tooltip/>
        </PieChart>
      </ResponsiveContainer>
    );
    if (chartType==='radar') return (
      <ResponsiveContainer width="100%" height={h}>
        <RadarChart data={depts.length ? depts.map(d=>({ dept:d.name.substring(0,4), total:d.total, complete:d.complete, oos:d.oos })) : [{ dept:'—', total:0, complete:0, oos:0 }]}>
          <PolarGrid stroke={PL}/>
          <PolarAngleAxis dataKey="dept" tick={{ fontSize:9, fill:'#64748B' }}/>
          <PolarRadiusAxis tick={{ fontSize:8, fill:'#94A3B8' }}/>
          <Radar name="Total"    dataKey="total"    stroke={PM} fill={PM} fillOpacity={0.2}/>
          <Radar name="Complete" dataKey="complete" stroke={GR} fill={GR} fillOpacity={0.2}/>
          <Radar name="OOS"      dataKey="oos"      stroke={RD} fill={RD} fillOpacity={0.3}/>
          <Tooltip/><Legend wrapperStyle={{ fontSize:'10px' }}/>
        </RadarChart>
      </ResponsiveContainer>
    );
    return null;
  };

  return (
    <div style={{ minHeight:'100vh', background:'#F5F3FF', paddingBottom:isMobile?'90px':'40px' }}>

      {/* ── COMPACT TOP NAV ── */}
      <div style={{ background:`linear-gradient(135deg,${P},${PM})`, padding:'0 12px', display:'flex', alignItems:'center', gap:'6px', height:'52px', position:'sticky', top:0, zIndex:100, boxShadow:'0 2px 12px rgba(107,33,168,0.35)', overflowX:'auto' }}>
        <button onClick={()=>navigate('/inspection/detergent')} style={{ padding:'5px 9px', background:'rgba(255,255,255,0.12)', border:'none', borderRadius:'7px', color:'#fff', fontSize:'11px', fontWeight:'700', cursor:'pointer', whiteSpace:'nowrap', flexShrink:0 }}>🔍 Inspect</button>

        <div ref={reportsRef} style={{ position:'relative', flexShrink:0 }}>
          <button onClick={()=>{ setShowReports(!showReports); setShowAdmin(false); }} style={{ padding:'5px 9px', background:showReports?'rgba(255,255,255,0.25)':'rgba(255,255,255,0.12)', border:'none', borderRadius:'7px', color:'#fff', fontSize:'11px', fontWeight:'700', cursor:'pointer', whiteSpace:'nowrap' }}>📋 Reports {showReports?'▲':'▼'}</button>
          {showReports && <DropdownMenu items={REPORT_ITEMS}/>}
        </div>

        <div ref={adminRef} style={{ position:'relative', flexShrink:0 }}>
          <button onClick={()=>{ setShowAdmin(!showAdmin); setShowReports(false); }} style={{ padding:'5px 9px', background:showAdmin?'rgba(255,255,255,0.25)':'rgba(255,255,255,0.12)', border:'none', borderRadius:'7px', color:'#fff', fontSize:'11px', fontWeight:'700', cursor:'pointer', whiteSpace:'nowrap' }}>⚙️ Admin {showAdmin?'▲':'▼'}</button>
          {showAdmin && <DropdownMenu items={ADMIN_ITEMS}/>}
        </div>

        {/* Quick actions — moved to top */}
        <button onClick={()=>navigate('/register-sample')} style={{ padding:'5px 9px', background:'rgba(255,255,255,0.12)', border:'none', borderRadius:'7px', color:'#fff', fontSize:'11px', fontWeight:'700', cursor:'pointer', whiteSpace:'nowrap', flexShrink:0 }}>+ Register</button>
        <button onClick={()=>setShowAssign(true)} style={{ padding:'5px 9px', background:'#FFB81C', border:'none', borderRadius:'7px', color:'#1F2937', fontSize:'11px', fontWeight:'800', cursor:'pointer', whiteSpace:'nowrap', flexShrink:0 }}>+ Assign</button>

        <div style={{ flex:1 }}/>
        <SessionTimer/>
        <div style={{ width:'28px', height:'28px', borderRadius:'50%', background:'rgba(255,255,255,0.2)', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontSize:'12px', fontWeight:'800', cursor:'pointer', flexShrink:0 }} onClick={logout}>
          {user?.full_name?.[0]?.toUpperCase()||'Q'}
        </div>
      </div>

      <div style={{ padding:isMobile?'10px':'16px', maxWidth:'1200px', margin:'0 auto' }}>

        {/* ── COMPACT HEADER + DATE FILTER IN ONE ROW ── */}
        <div style={{ display:'flex', alignItems:'center', gap:'8px', flexWrap:'wrap', marginBottom:'10px', background:'#fff', borderRadius:'12px', border:`1.5px solid ${PL}`, padding:'8px 12px' }}>
          <div style={{ flexShrink:0 }}>
            <div style={{ fontSize:'14px', fontWeight:'900', color:'#0F172A' }}>Factory QC Dashboard</div>
            <div style={{ fontSize:'10px', color:'#94A3B8' }}>{format(new Date(),'EEE, d MMM yyyy')}</div>
          </div>
          <div style={{ width:'1px', height:'32px', background:'#E2E8F0', flexShrink:0 }}/>
          {/* Date mode toggle */}
          <div style={{ display:'flex', borderRadius:'7px', overflow:'hidden', border:`1.5px solid ${PL}`, flexShrink:0 }}>
            {['day','range'].map(m=>(
              <button key={m} onClick={()=>setDateMode(m)} style={{ padding:'4px 10px', border:'none', background:dateMode===m?PM:'#fff', color:dateMode===m?'#fff':'#6B7280', fontSize:'11px', fontWeight:'700', cursor:'pointer' }}>{m==='day'?'Day':'Range'}</button>
            ))}
          </div>
          {dateMode==='day'
            ? <input type="date" value={selDate} onChange={e=>setSelDate(e.target.value)} style={{ border:`1.5px solid ${PL}`, borderRadius:'7px', padding:'4px 8px', fontSize:'11px', cursor:'pointer' }}/>
            : <><input type="date" value={fromDate} onChange={e=>setFromDate(e.target.value)} style={{ border:`1.5px solid ${PL}`, borderRadius:'7px', padding:'4px 8px', fontSize:'11px' }}/><span style={{ color:'#94A3B8', fontSize:'11px' }}>→</span><input type="date" value={toDate} onChange={e=>setToDate(e.target.value)} style={{ border:`1.5px solid ${PL}`, borderRadius:'7px', padding:'4px 8px', fontSize:'11px' }}/></>
          }
          {['Today','Yest','Week'].map((lbl,i)=>(
            <button key={lbl} onClick={()=>{ if(i===2){setDateMode('range');setFromDate(format(subDays(new Date(),6),'yyyy-MM-dd'));setToDate(format(new Date(),'yyyy-MM-dd'));}else{setDateMode('day');setSelDate(format(subDays(new Date(),[0,1,0][i]),'yyyy-MM-dd'));} }}
              style={{ padding:'4px 9px', border:`1.5px solid ${PL}`, borderRadius:'7px', background:'#F5F3FF', color:P, fontSize:'11px', fontWeight:'700', cursor:'pointer', flexShrink:0 }}>
              {lbl}
            </button>
          ))}
          <span style={{ fontSize:'11px', color:'#94A3B8', marginLeft:'auto', flexShrink:0 }}>{stats.total} samples</span>
        </div>

        {/* ── SLIM STATS BAR (single row, pill-style) ── */}
        <div style={{ display:'flex', gap:'6px', marginBottom:'10px', overflowX:'auto', paddingBottom:'2px' }}>
          {[
            { label:'Total',    val:stats.total,    color:'#1E293B', bg:'#fff',    border:'#E2E8F0' },
            { label:'Pending',  val:stats.pending,  color:'#64748B', bg:'#F8FAFC', border:'#E2E8F0' },
            { label:'Running',  val:stats.running,  color:AM,        bg:'#FFFBEB', border:'#FDE68A' },
            { label:'Done',     val:stats.complete, color:GR,        bg:'#F0FDF4', border:'#86EFAC' },
            { label:'OOS',      val:stats.oos,      color:RD,        bg:'#FEF2F2', border:'#FECACA' },
            { label:'Pass%',    val:`${stats.passRate}%`, color:stats.passRate>=95?GR:stats.passRate>=80?AM:RD, bg:'#F5F3FF', border:PL },
            { label:'Stale',    val:stats.stale,    color:stats.stale>0?AM:'#94A3B8', bg:stats.stale>0?'#FFFBEB':'#fff', border:stats.stale>0?'#FDE68A':'#E2E8F0' },
          ].map(s=>(
            <div key={s.label} style={{ background:s.bg, border:`1.5px solid ${s.border}`, borderRadius:'20px', padding:'4px 12px', textAlign:'center', flexShrink:0, display:'flex', alignItems:'center', gap:'5px' }}>
              <span style={{ fontSize:'14px', fontWeight:'900', color:s.color }}>{s.val}</span>
              <span style={{ fontSize:'9px', color:'#94A3B8', fontWeight:'700', textTransform:'uppercase' }}>{s.label}</span>
            </div>
          ))}
        </div>

        {/* ── ANALYTICS SECTION (main content) ── */}
        <div style={{ background:'#fff', borderRadius:'14px', border:`1.5px solid ${PL}`, padding:'14px', marginBottom:'10px' }}>
          {/* Chart type selector */}
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'12px', flexWrap:'wrap', gap:'8px' }}>
            <div style={{ fontWeight:'800', fontSize:'13px', color:'#0F172A' }}>
              📊 7-Day Sample Analytics
            </div>
            <div style={{ display:'flex', gap:'4px', flexWrap:'wrap' }}>
              {CHART_TYPES.map(ct=>(
                <button key={ct.key} onClick={()=>setChartType(ct.key)}
                  style={{ padding:'5px 10px', borderRadius:'20px', border:'none', background:chartType===ct.key?`linear-gradient(135deg,${P},${PM})`:'#F1F5F9', color:chartType===ct.key?'#fff':'#64748B', fontSize:'11px', fontWeight:'700', cursor:'pointer' }}>
                  {ct.icon} {ct.label}
                </button>
              ))}
            </div>
          </div>
          {loading
            ? <div style={{ textAlign:'center', padding:'40px', color:'#94A3B8' }}>Loading...</div>
            : renderTrendChart()
          }
        </div>

        {/* ── DEPT STATUS + OOS FEED side by side ── */}
        <div style={{ display:'grid', gridTemplateColumns:isMobile?'1fr':'1fr 300px', gap:'10px', marginBottom:'10px' }}>

          {/* Department status — compact cards */}
          <div style={{ background:'#fff', borderRadius:'14px', border:`1.5px solid ${PL}`, padding:'12px' }}>
            <div style={{ fontWeight:'800', fontSize:'12px', color:'#0F172A', marginBottom:'10px' }}>🏭 Department Status</div>
            {loading ? <div style={{ textAlign:'center', padding:'20px', color:'#94A3B8', fontSize:'12px' }}>Loading...</div>
            : depts.length===0 ? <div style={{ textAlign:'center', padding:'20px', color:'#94A3B8', fontSize:'12px' }}>No samples for this period</div>
            : depts.map(d => {
              const cfg = DEPT_COLORS[d.name] || { bg:'#F8FAFC', color:'#475569', border:'#E2E8F0' };
              const pct = d.total ? Math.round((d.complete/d.total)*100) : 0;
              return (
                <div key={d.name} style={{ display:'flex', alignItems:'center', gap:'10px', padding:'7px 0', borderBottom:`1px solid #F1F5F9` }}>
                  <div style={{ width:'8px', height:'8px', borderRadius:'50%', background:cfg.color, flexShrink:0 }}/>
                  <div style={{ fontSize:'12px', fontWeight:'700', color:'#0F172A', flex:1 }}>{d.name}</div>
                  <div style={{ display:'flex', gap:'4px', alignItems:'center' }}>
                    {d.oos>0 && <span style={{ background:'#FEE2E2', color:RD, fontSize:'9px', fontWeight:'800', padding:'1px 5px', borderRadius:'4px' }}>⚠ {d.oos}</span>}
                    <span style={{ fontSize:'11px', color:'#64748B', fontWeight:'600' }}>{d.complete}/{d.total}</span>
                  </div>
                  <div style={{ width:'60px', background:'#F1F5F9', borderRadius:'4px', height:'5px', overflow:'hidden' }}>
                    <div style={{ height:'100%', width:`${pct}%`, background:d.oos>0?RD:GR, borderRadius:'4px' }}/>
                  </div>
                  <span style={{ fontSize:'10px', color:'#94A3B8', minWidth:'28px', textAlign:'right' }}>{pct}%</span>
                </div>
              );
            })}
          </div>

          {/* OOS Feed */}
          <div style={{ background:'#FEF2F2', borderRadius:'14px', border:'1.5px solid #FECACA', padding:'12px' }}>
            <div style={{ fontWeight:'800', fontSize:'12px', color:RD, marginBottom:'8px', display:'flex', justifyContent:'space-between' }}>
              <span>⚠️ OOS Feed</span>
              <span style={{ fontSize:'10px', color:'#94A3B8', fontWeight:'600' }}>{oosFeed.length} alerts</span>
            </div>
            {oosFeed.length===0
              ? <div style={{ textAlign:'center', padding:'20px', color:GR, fontWeight:'700', fontSize:'12px' }}>✅ No OOS results</div>
              : <div style={{ maxHeight:'260px', overflowY:'auto' }}>
                  {oosFeed.map((o,i)=>(
                    <div key={i} style={{ background:'#fff', borderRadius:'8px', padding:'8px', border:'1px solid #FECACA', marginBottom:'5px' }}>
                      <div style={{ fontWeight:'700', fontSize:'11.5px', color:'#0F172A' }}>{o.sample_name}</div>
                      <div style={{ display:'flex', justifyContent:'space-between', marginTop:'2px' }}>
                        <span style={{ fontSize:'10.5px', color:RD, fontWeight:'700' }}>{o.test_name}: {o.result} {o.status==='fail_low'?'↓ LOW':'↑ HIGH'}</span>
                        <span style={{ fontSize:'9.5px', color:'#94A3B8' }}>{o.time?format(new Date(o.time),'HH:mm'):''}</span>
                      </div>
                    </div>
                  ))}
                </div>
            }
          </div>
        </div>

        {/* ── STALE SAMPLES (only when present) ── */}
        {staleSamples.length>0 && (
          <div style={{ background:'#fff', borderRadius:'12px', border:'1.5px solid #FED7AA', padding:'12px', marginBottom:'10px' }}>
            <div style={{ fontWeight:'800', fontSize:'12px', color:AM, marginBottom:'8px' }}>⏰ Stale Samples (&gt;2hrs no activity)</div>
            {staleSamples.map(s=>(
              <div key={s.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'6px 10px', background:'#FFFBEB', borderRadius:'7px', border:'1px solid #FDE68A', marginBottom:'5px' }}>
                <div>
                  <div style={{ fontWeight:'700', fontSize:'12px', color:'#0F172A' }}>{s.sample_name}</div>
                  <div style={{ fontSize:'10px', color:'#94A3B8' }}>{s.sample_number}</div>
                </div>
                <button onClick={()=>navigate(`/analysis/${s.id}`)} style={{ padding:'4px 9px', background:AM, color:'#fff', border:'none', borderRadius:'6px', fontSize:'11px', fontWeight:'700', cursor:'pointer' }}>Open →</button>
              </div>
            ))}
          </div>
        )}

      </div>

      {/* ════ ASSIGN SAMPLE MODAL ════ */}
      {showAssign && (
        <div onClick={e=>{if(e.target===e.currentTarget){setShowAssign(false);setAssignStep(1);setAssignSample(null);setAssignNew(false);}}}
          style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.55)', zIndex:600, display:'flex', alignItems:isMobile?'flex-end':'center', justifyContent:'center', padding:isMobile?0:16 }}>
          <div style={{ background:'#fff', borderRadius:isMobile?'20px 20px 0 0':'18px', width:'100%', maxWidth:'540px', maxHeight:isMobile?'90vh':'85vh', overflow:'hidden', display:'flex', flexDirection:'column', boxShadow:'0 24px 80px rgba(0,0,0,0.3)' }}>
            <div style={{ background:`linear-gradient(135deg,${P},${PM})`, padding:'14px 18px', color:'#fff', flexShrink:0 }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <div>
                  <div style={{ fontWeight:'900', fontSize:'15px' }}>📋 Assign Sample</div>
                  <div style={{ fontSize:'10px', color:'#DDD6FE', marginTop:'1px' }}>Step {assignStep} of 3 — {['','Choose Sample','Analyst & Tests','Confirm'][assignStep]}</div>
                </div>
                <button onClick={()=>{setShowAssign(false);setAssignStep(1);setAssignSample(null);setAssignNew(false);}} style={{ background:'rgba(255,255,255,0.15)', border:'none', borderRadius:'7px', color:'#fff', fontSize:'16px', cursor:'pointer', width:'28px', height:'28px' }}>✕</button>
              </div>
              <div style={{ display:'flex', gap:'4px', marginTop:'10px' }}>
                {[1,2,3].map(s=><div key={s} style={{ height:'3px', flex:1, borderRadius:'3px', background:s<=assignStep?'rgba(255,255,255,0.9)':'rgba(255,255,255,0.25)' }}/>)}
              </div>
            </div>

            <div style={{ flex:1, overflowY:'auto', padding:'14px 16px' }}>
              {assignStep===1 && (
                <>
                  <div style={{ display:'flex', gap:'6px', marginBottom:'12px' }}>
                    {[['🔍 Find Existing',false],['✨ New Sample',true]].map(([lbl,v])=>(
                      <button key={lbl} onClick={()=>setAssignNew(v)} style={{ flex:1, padding:'8px', border:`1.5px solid ${assignNew===v?PM:PL}`, borderRadius:'8px', background:assignNew===v?PL:'#fff', color:assignNew===v?P:'#6B7280', fontSize:'12px', fontWeight:'700', cursor:'pointer' }}>{lbl}</button>
                    ))}
                  </div>
                  {!assignNew ? (
                    <>
                      <input type="text" value={assignSearch} onChange={e=>{ setAssignSearch(e.target.value); searchSamples(e.target.value); }} placeholder="Search sample name or number..." style={{ ...inp, marginBottom:'8px' }}/>
                      {assignSamples.map(s=>(
                        <div key={s.id} onClick={()=>setAssignSample(s)} style={{ padding:'9px 12px', borderRadius:'9px', cursor:'pointer', marginBottom:'5px', border:`1.5px solid ${assignSample?.id===s.id?PM:PL}`, background:assignSample?.id===s.id?PL:'#fff' }}>
                          <div style={{ fontWeight:'700', fontSize:'13px', color:'#0F172A' }}>{s.sample_name}</div>
                          <div style={{ fontSize:'11px', color:'#94A3B8' }}>{s.sample_number} · {s.departments?.name}</div>
                        </div>
                      ))}
                      {assignSearch && !assignSamples.length && <div style={{ textAlign:'center', color:'#94A3B8', fontSize:'12px', padding:'16px' }}>No pending samples found</div>}
                    </>
                  ) : (
                    <div style={{ display:'flex', flexDirection:'column', gap:'10px' }}>
                      {[{label:'Sample Name *',key:'sample_name',ph:'e.g. Base Powder'},{label:'Sampler Name',key:'sampler_name',ph:'Who collected this'}].map(f=>(
                        <div key={f.key}>
                          <label style={{ display:'block', fontSize:'11px', fontWeight:'700', color:'#4C1D95', marginBottom:'3px' }}>{f.label}</label>
                          <input type="text" value={newSampleForm[f.key]||''} onChange={e=>setNewSampleForm(p=>({...p,[f.key]:e.target.value}))} placeholder={f.ph} style={inp}/>
                        </div>
                      ))}
                      <div>
                        <label style={{ display:'block', fontSize:'11px', fontWeight:'700', color:'#4C1D95', marginBottom:'3px' }}>Department *</label>
                        <select value={newSampleForm.dept_id} onChange={e=>setNewSampleForm(p=>({...p,dept_id:e.target.value}))} style={{ ...inp, cursor:'pointer' }}>
                          <option value="">— Select —</option>
                          {deptList.map(d=><option key={d.id} value={d.id}>{d.name}</option>)}
                        </select>
                      </div>
                      <div>
                        <label style={{ display:'block', fontSize:'11px', fontWeight:'700', color:'#4C1D95', marginBottom:'3px' }}>Sample Type *</label>
                        <select value={newSampleForm.type_id} onChange={e=>setNewSampleForm(p=>({...p,type_id:e.target.value}))} style={{ ...inp, cursor:'pointer' }}>
                          <option value="">— Select —</option>
                          {typeList.map(t=><option key={t.id} value={t.id}>{t.name}</option>)}
                        </select>
                      </div>
                    </div>
                  )}
                </>
              )}

              {assignStep===2 && (
                <>
                  <div style={{ marginBottom:'12px' }}>
                    <label style={{ display:'block', fontSize:'11px', fontWeight:'700', color:'#4C1D95', marginBottom:'4px' }}>Analyst *</label>
                    <select value={assignAnalyst} onChange={e=>setAssignAnalyst(e.target.value)} style={{ ...inp, cursor:'pointer' }}>
                      <option value="">— Select Analyst —</option>
                      {staffList.map(s=><option key={s.id} value={s.full_name}>{s.full_name}</option>)}
                    </select>
                  </div>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'8px' }}>
                    <label style={{ fontSize:'11px', fontWeight:'700', color:'#4C1D95' }}>Tests ({selTests.length} selected)</label>
                    <button onClick={()=>setSelTests(selTests.length===availTests.length?[]:availTests.map(t=>t.id))} style={{ background:'none', border:'none', color:PM, fontSize:'11px', fontWeight:'700', cursor:'pointer' }}>{selTests.length===availTests.length?'Deselect All':'Select All'}</button>
                  </div>
                  {availTests.length===0
                    ? <div style={{ padding:'16px', textAlign:'center', color:'#94A3B8', fontSize:'12px', background:'#F8FAFC', borderRadius:'9px' }}>No tests for this sample type</div>
                    : availTests.map(t=>{
                      const checked = selTests.includes(t.id);
                      return (
                        <label key={t.id} onClick={()=>setSelTests(p=>p.includes(t.id)?p.filter(x=>x!==t.id):[...p,t.id])} style={{ display:'flex', alignItems:'center', gap:'8px', padding:'8px 11px', border:`1.5px solid ${checked?PM:PL}`, borderRadius:'8px', cursor:'pointer', background:checked?PL:'#fff', marginBottom:'5px' }}>
                          <input type="checkbox" checked={checked} readOnly style={{ width:'14px', height:'14px', accentColor:PM }}/>
                          <div style={{ flex:1 }}>
                            <div style={{ fontWeight:'700', fontSize:'12px', color:'#0F172A' }}>{t.name}</div>
                            {t.test_specifications?.[0]?.display_spec && <div style={{ fontSize:'10px', color:AM }}>Spec: {t.test_specifications[0].display_spec}</div>}
                          </div>
                          {t.unit && <span style={{ fontSize:'10px', color:'#94A3B8' }}>{t.unit}</span>}
                          {checked && <span style={{ color:GR, fontSize:'12px' }}>✓</span>}
                        </label>
                      );
                    })
                  }
                </>
              )}

              {assignStep===3 && (
                <div style={{ background:'#F0FDF4', border:'1.5px solid #86EFAC', borderRadius:'12px', padding:'14px' }}>
                  <div style={{ fontWeight:'800', fontSize:'14px', color:GR, marginBottom:'10px' }}>✅ Ready to Assign</div>
                  <div style={{ fontSize:'13px', color:'#374151', display:'flex', flexDirection:'column', gap:'6px' }}>
                    <div><strong>Sample:</strong> {assignNew?newSampleForm.sample_name:assignSample?.sample_name}</div>
                    <div><strong>Analyst:</strong> {assignAnalyst}</div>
                    <div><strong>Tests ({selTests.length}):</strong> {availTests.filter(t=>selTests.includes(t.id)).map(t=>t.name).join(', ')}</div>
                  </div>
                </div>
              )}
            </div>

            <div style={{ padding:'12px 16px', paddingBottom: isMobile ? 'calc(12px + 80px)' : '12px', borderTop:`1.5px solid ${PL}`, background:'#F9FAFB', display:'flex', gap:'8px', flexShrink:0 }}>
              {assignStep>1 && <button onClick={()=>setAssignStep(p=>p-1)} style={{ padding:'10px 16px', background:'#F1F5F9', color:'#374151', border:'none', borderRadius:'9px', fontSize:'13px', fontWeight:'600', cursor:'pointer' }}>← Back</button>}
              {assignStep<3
                ? <button onClick={()=>{
                    if(assignStep===1){ if(!assignNew&&!assignSample){toast.warning('Select a sample');return;} if(assignNew&&(!newSampleForm.sample_name||!newSampleForm.dept_id||!newSampleForm.type_id)){toast.warning('Fill all required fields');return;} }
                    if(assignStep===2){ if(!assignAnalyst){toast.warning('Select an analyst');return;} if(!selTests.length){toast.warning('Select at least one test');return;} }
                    setAssignStep(p=>p+1);
                  }} style={{ flex:1, padding:'10px', background:`linear-gradient(135deg,${P},${PM})`, color:'#fff', border:'none', borderRadius:'9px', fontSize:'13px', fontWeight:'700', cursor:'pointer' }}>Next →</button>
                : <button onClick={handleAssign} disabled={assigning} style={{ flex:1, padding:'10px', background:'linear-gradient(135deg,#059669,#047857)', color:'#fff', border:'none', borderRadius:'9px', fontSize:'13px', fontWeight:'700', cursor:assigning?'not-allowed':'pointer' }}>{assigning?'⏳ Assigning...':'✅ Confirm Assignment'}</button>
              }
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SessionTimer() {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const start = Date.now();
    const iv = setInterval(()=>setElapsed(Math.floor((Date.now()-start)/1000)),1000);
    return ()=>clearInterval(iv);
  }, []);
  const h=Math.floor(elapsed/3600), m=Math.floor((elapsed%3600)/60), s=elapsed%60;
  return <span style={{ background:'rgba(255,255,255,0.12)', borderRadius:'7px', padding:'4px 8px', color:'#fff', fontSize:'10px', fontWeight:'700', fontFamily:'monospace', flexShrink:0 }}>⏱ {String(h).padStart(2,'0')}:{String(m).padStart(2,'0')}:{String(s).padStart(2,'0')}</span>;
}
